import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import './AddUsers.scss';
import { deleteAddedUser } from '../../services/api/AddUsersApi';
import { useLoading } from '../../context/LoadingContext';
import { useToast } from "../../context/ToastContext";
import { BiEdit, BiTrash } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getHierarchy } from '../../services/api/HierarchyApi';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal/DeleteConfirmationModal';
import { useSearchResults } from '../../context/SearchResultsContext';
import { useUserSelection } from '../../context/UserSelectionContext';
import { IoGrid } from "react-icons/io5";
import { FaTableList } from "react-icons/fa6";
import { formatText } from '../../components/TruncateParties';

export const AddUsers = () => {
  const navigate = useNavigate()
  const { role: currentUserRole } = useAuth();
  const { searchResults, searchType } = useSearchResults();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedLawyer, setSelectedLawyer] = useState(null);
  const { showLoading, hideLoading } = useLoading();
  const { showToast } = useToast();
  const [lawyers, setLawyers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [viewMode, setViewMode] = useState("table");

  // Use UserSelectionContext instead of local state
  const {
    searchTerm: userSearchTerm,
    setSearchTerm: setUserSearchTerm,
    filters: userFilters,
    setFilters: setUserFilters,
    filteredUsers,
    setFilteredUsers,
    hasActiveFilters: hasUserFilters,
    clearAllFilters: clearUserFilters,
    triggerUserRefresh,
    pagination,
    updatePaginationInfo,
    setPagination,
    selectedCard,
    handleCardClick
  } = useUserSelection();

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Helper function to check if current user can perform admin actions
  const canPerformAdminActions = () => {
    return currentUserRole === 'admin' || currentUserRole === 'superAdmin';
  };

  // Helper function to check if user details should be accessible
  const isUserAccessible = (userRole) => {
    // Admin and superAdmin can access all users
    if (currentUserRole === 'admin' || currentUserRole === 'superAdmin') return true;
    // Non-admin can only access non-admin and non-superAdmin users
    return userRole !== 'admin' && userRole !== 'superAdmin';
  };

  const displayUsers = useMemo(() => {
    // Use filteredUsers from context for unified behavior
    return filteredUsers || [];
  }, [filteredUsers]);

  const finalDisplayUsers = useMemo(() => {
    // Apply local UI filters on top of context-provided filtered users
    return displayUsers.filter((lawyer) => {
      // Only apply filters that aren't handled by the unified system
      return true; // For now, let context handle all filtering
    });
  }, [displayUsers]);

  const shouldShowNoDataMessage = () => {
    return displayUsers.length === 0;
  };

  const getNoDataMessage = () => {
    const hasContextFilters = hasUserFilters;

    if (hasContextFilters && displayUsers.length === 0) {
      return "No users found matching your search and selections.";
    }

    return "No users found. Start by adding a new one!";
  };




  const handleUsersPerPageChange = useCallback((e) => {
    const newUsersPerPage = e.target.value === "all" ? "all" : Number(e.target.value);
    setPagination(prev => ({
      ...prev,
      limit: newUsersPerPage,
      page: 0
    }));
  }, [setPagination]);


  const handlePageChange = useCallback((newPage) => {

    setPagination(prev => ({
      ...prev,
      page: newPage
    }));
  }, [setPagination]);

  const handleEditClickUsers = (e, lawyer) => {
    e.stopPropagation();
    navigate(`/home/addusers/edit-user/${lawyer._id}`);
  };

  const handleDeleteClickUsers = (e, lawyer) => {
    e.stopPropagation();
    setSelectedLawyer(lawyer);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedLawyer) return;
    try {
      await deleteAddedUser(selectedLawyer._id);
      showToast("User deleted successfully.", "success");

      // Refresh the user list
      const res = await getHierarchy();
      const userData = res.data.users || [];
      setLawyers(userData);

      // FIXED: Update context with fresh data after delete
      setFilteredUsers(userData);
      if (filteredUsers.length === 1 && pagination.page > 0) {
        handlePageChange(pagination.page - 1);
      } else {
        triggerUserRefresh(); // Otherwise, just refresh the current page
      }



    } catch (error) {
      showToast("Failed to delete user.", "error");
    } finally {
      setShowDeleteModal(false);
      setSelectedLawyer(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setSelectedLawyer(null);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".filter-dropdown-wrapper")) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filterButtonRef = useRef();
  const filterDropdownRef = useRef();
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [dropdownOverflow, setDropdownOverflow] = useState('auto');

  useEffect(() => {
    if (isFilterOpen && filterButtonRef.current) {
      const buttonRect = filterButtonRef.current.getBoundingClientRect();
      const dropdownWidth = 365;
      const dropdownMargin = 4;
      let leftPosition = buttonRect.left;
      if (leftPosition + dropdownWidth > window.innerWidth) {
        leftPosition = buttonRect.right - dropdownWidth;
      }
      if (leftPosition < 0) {
        leftPosition = 10;
      }
      // Always show below the button, but limit maxHeight if needed
      setDropdownPosition({
        top: buttonRect.bottom + dropdownMargin,
        left: leftPosition,
        maxHeight: 300,
      });
    }

    const handleScroll = (e) => {
      if (!isFilterOpen) return;
      // Only close if scroll is outside the dropdown
      const dropdown = document.querySelector('.filter-dropdown-box');
      if (dropdown && e && e.target && dropdown.contains(e.target)) {
        // Scrolling inside the dropdown, do nothing
        return;
      }
      setIsFilterOpen(false);
    };

    if (isFilterOpen) {
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isFilterOpen]);

  // Check if dropdown content is overflowing and set overflowY accordingly
  useEffect(() => {
    if (isFilterOpen && filterDropdownRef.current) {
      const el = filterDropdownRef.current;
      if (el.scrollHeight > el.clientHeight) {
        setDropdownOverflow('auto');
      } else {
        setDropdownOverflow('visible');
      }
    }
  }, [isFilterOpen, dropdownPosition]);

  return (
    <>
      <div className={`lawyer-list`}>
        <div className='spreadsheet-filter-header'>
          <div className='invoices-header'>
            <div className="view-toggle-wrapper-user">
              <button
                className={`view-toggle-btn ${viewMode === "grid" ? "active" : ""}`}
                onClick={() => setViewMode("grid")}
              >
                <IoGrid />
              </button>
              <button
                className={`view-toggle-btn ${viewMode === "table" ? "active" : ""}`}
                onClick={() => setViewMode("table")}
              >
                <FaTableList />
              </button>
            </div>

            <div className="common-pagination-settings">
              <label htmlFor="userPerPageSelect">Users per page:</label>
              <select
                id="userPerPageSelect"
                value={pagination.limit}
                onChange={handleUsersPerPageChange}
              >
                {[5, 10, 15, 20, 25, 50].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
                <option value="all">All</option>
              </select>
            </div>

            <div className="filter-dropdown-wrapper">
              <button className="filter-toggle-btn" onClick={() => setIsFilterOpen(prev => !prev)} ref={filterButtonRef}>
                {isFilterOpen ? "Hide Filters" : "Show Filters"}
              </button>

              {isFilterOpen && dropdownPosition.top !== 0 && dropdownPosition.left !== 0 && (
                <div className="filter-dropdown-box"
                  ref={filterDropdownRef}
                  style={{
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    maxHeight: dropdownPosition.maxHeight ? `${dropdownPosition.maxHeight}px` : '500px',
                    overflowY: dropdownOverflow,
                    position: 'fixed',
                  }}
                >
                  <div className="filter-group">
                    <label>Name:</label>
                    <input
                      type="text"
                      placeholder='Enter Name'
                      value={userFilters.name}
                      onChange={(e) => setUserFilters({ ...userFilters, name: e.target.value })}
                    />
                  </div>
                  <div className="filter-group">
                    <label>Email:</label>
                    <input
                      type="text"
                      placeholder='Enter Email'
                      value={userFilters.email}
                      onChange={(e) => setUserFilters({ ...userFilters, email: e.target.value })}
                    />
                  </div>
                  <div className="filter-group">
                    <label>Phone No:</label>
                    <input
                      type="text"
                      placeholder='Enter Phone No'
                      value={userFilters.phno}
                      onChange={(e) => setUserFilters({ ...userFilters, phno: e.target.value })}
                    />
                  </div>
                  <div className="filter-group">
                    <label>Role:</label>
                    <select
                      value={userFilters.role}
                      onChange={(e) => setUserFilters({ ...userFilters, role: e.target.value })}
                    >
                      <option value="">All</option>
                      <option value="admin">Admin</option>
                      <option value="moderator">Moderator</option>
                      <option value="lawyer">Lawyer</option>
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>Status:</label>
                    <select
                      value={userFilters.status}
                      onChange={(e) => setUserFilters({ ...userFilters, status: e.target.value })}
                    >
                      <option value="">All</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div className="filter-actions">
                    <button
                      className="clear-btn"
                      onClick={() => setUserFilters({ name: "", email: "", phno: "", role: "", status: "" })}
                    >
                      Clear
                    </button>
                    <button className="hide-btn" onClick={() => setIsFilterOpen(false)}>Hide</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Show applied filters if any */}
        {/* {(Object.values(userFilters).some(val => val) || hasUserFilters) && (
        <div className="applied-filters">
          <strong>Applied Filters:</strong>
          {userSearchTerm && <span>Search: {userSearchTerm}</span>}
          {userFilters.name && <span>Name: {userFilters.name}</span>}
          {userFilters.email && <span>Email: {userFilters.email}</span>}
          {userFilters.phno && <span>Phone: {userFilters.phno}</span>}
          {userFilters.role && <span>Role: {userFilters.role}</span>}
          {userFilters.status && <span>Status: {userFilters.status}</span>}
        </div>
      )} */}
        {(Object.values(userFilters).some(val => val) || hasUserFilters) && (
          <div className="applied-filters">
            <span className="filters-text">Applied Filters:</span>

            {/* Search term filter pill */}
            {userSearchTerm && userSearchTerm.trim() && (
              <span
                className="filter-tag"
                onClick={() => {
                  setUserSearchTerm('');
                  // Clear DetailSection's search input
                  if (window.clearDetailSectionSearch) {
                    window.clearDetailSectionSearch();
                  }
                }}
              >
                Search: {formatText(userSearchTerm)} ×
              </span>
            )}

            {selectedCard && (
              <span
                className="filter-tag"
                onClick={() => handleCardClick(selectedCard)}
              >
                Card: {formatText(selectedCard)} ×
              </span>
            )}

            {/* Individual filter pills */}
            {userFilters.name && (
              <span
                className="filter-tag"
                onClick={() => setUserFilters({ ...userFilters, name: '' })}
              >
                Name: {formatText(userFilters.name)} ×
              </span>
            )}

            {userFilters.email && (
              <span
                className="filter-tag"
                onClick={() => setUserFilters({ ...userFilters, email: '' })}
              >
                Email: {formatText(userFilters.email)} ×
              </span>
            )}

            {userFilters.phno && (
              <span
                className="filter-tag"
                onClick={() => setUserFilters({ ...userFilters, phno: '' })}
              >
                Phone: {formatText(userFilters.phno)} ×
              </span>
            )}

            {userFilters.role && (
              <span
                className="filter-tag"
                onClick={() => setUserFilters({ ...userFilters, role: '' })}
              >
                Role: {formatText(userFilters.role)} ×
              </span>
            )}

            {userFilters.status && (
              <span
                className="filter-tag"
                onClick={() => setUserFilters({ ...userFilters, status: '' })}
              >
                Status: {formatText(userFilters.status)} ×
              </span>
            )}

            {/* Clear All button */}
            <span
              className="filter-tag clear-all-tag"
              onClick={() => {
                clearUserFilters();
                // Clear DetailSection's search input  
                if (window.clearDetailSectionSearch) {
                  window.clearDetailSectionSearch();
                }
              }}
            >
              Clear All
            </span>
          </div>
        )}

        {shouldShowNoDataMessage() ? (
          <p className='no-lawyers-found'>
            {getNoDataMessage()}
          </p>
        ) : viewMode === "grid" ? (
          <div className="lawyer-cards">
            {displayUsers.map((lawyer, index) => (
              <div 
                className="lawyer-card" 
                key={index} 
                onClick={() => navigate(`/home/addusers/${lawyer._id}`)}
                style={{ cursor: 'pointer' }}
              >
                <div className="card-header">
                  <h3>{lawyer.name}</h3>
                  <span className={`status-badge ${lawyer.isActive ? 'active' : 'inactive'}`}>
                    {lawyer.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="card-content">
                  <div className="info-item">
                    <span className="label">Email:</span>
                    <span className="value">{lawyer.email}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Phone:</span>
                    <span className="value">{lawyer.phno}</span>
                  </div>
                </div>

                <div className="card-footer">
                  <span className="role-badge">{lawyer.role}</span>
                  <div className='userscrud'>
                    <button 
                      onClick={(e) => handleEditClickUsers(e, lawyer)} 
                      className="btn edit" 
                      disabled={lawyer.role === 'admin' || lawyer.role === 'superAdmin'}
                      style={{ 
                        cursor: (lawyer.role === 'admin' || lawyer.role === 'superAdmin') ? 'not-allowed' : 'pointer',
                        opacity: (lawyer.role === 'admin' || lawyer.role === 'superAdmin') ? 0.5 : 1
                      }}
                    >
                      <BiEdit size={16} />
                    </button>
                    <button 
                      onClick={(e) => handleDeleteClickUsers(e, lawyer)} 
                      className="btn delete"
                      disabled={lawyer.role === 'admin' || lawyer.role === 'superAdmin'}
                      style={{ 
                        cursor: (lawyer.role === 'admin' || lawyer.role === 'superAdmin') ? 'not-allowed' : 'pointer',
                        opacity: (lawyer.role === 'admin' || lawyer.role === 'superAdmin') ? 0.5 : 1
                      }}
                    >
                      <BiTrash size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <table className="users-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Phone</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayUsers.map((lawyer, index) => (
                <tr 
                  key={index} 
                  onClick={() => navigate(`/home/addusers/${lawyer._id}`)} 
                  style={{ cursor: 'pointer' }}
                >
                  <td>{lawyer.name}</td>
                  <td>{lawyer.email}</td>
                  <td>
                    <span className="role-badge">{lawyer.role}</span>
                  </td>
                  <td>
                    <span className={`role-badge-status ${lawyer.isActive ? 'active' : 'inactive'}`}>
                      {lawyer.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>{lawyer.phno}</td>
                  <td>
                    <button 
                      onClick={(e) => handleEditClickUsers(e, lawyer)} 
                      disabled={lawyer.role === 'admin' || lawyer.role === 'superAdmin'}
                      style={{ 
                        cursor: (lawyer.role === 'admin' || lawyer.role === 'superAdmin') ? 'not-allowed' : 'pointer',
                        opacity: (lawyer.role === 'admin' || lawyer.role === 'superAdmin') ? 0.5 : 1
                      }}
                    >
                      Update
                    </button>
                    <button 
                      onClick={(e) => handleDeleteClickUsers(e, lawyer)} 
                      disabled={lawyer.role === 'admin' || lawyer.role === 'superAdmin'}
                      style={{ 
                        cursor: (lawyer.role === 'admin' || lawyer.role === 'superAdmin') ? 'not-allowed' : 'pointer',
                        opacity: (lawyer.role === 'admin' || lawyer.role === 'superAdmin') ? 0.5 : 1
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}



        {/* <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept=".xlsx,.xls"
        onChange={handleFileChange}
      /> */}
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          itemType={selectedLawyer?.name || "user"}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />

      </div>
      {pagination.limit !== "all" && (pagination.totalUsers > pagination.limit || pagination.page > 0) && (
        <div className="pagination-controls">
          <button
            disabled={pagination.page === 0}
            onClick={() => handlePageChange(0)}
          >
            First
          </button>
          <button
            disabled={pagination.page === 0}
            onClick={() => handlePageChange(pagination.page - 1)}
          >
            Previous
          </button>
          <span>
            Page {pagination.page + 1} of {Math.ceil(pagination.totalUsers / pagination.limit) || 1}
          </span>
          <button
            disabled={pagination.page >= Math.ceil(pagination.totalUsers / pagination.limit) - 1}
            onClick={() => handlePageChange(pagination.page + 1)}
          >
            Next
          </button>
          <button
            disabled={pagination.page >= Math.ceil(pagination.totalUsers / pagination.limit) - 1}
            onClick={() => handlePageChange(Math.ceil(pagination.totalUsers / pagination.limit) - 1)}
          >
            Last
          </button>
        </div>
      )}
    </>
  );
};